import type { PropertyFormValues } from './schema';
import type { PropertyNestedRequest } from '@services/housien/types-property';
import {
  mapFormAmenitiesToNested,
  mapFormCharacteristicsToNested,
} from '@/shared/utils/property-nested-write';
import {
  buildCondominium,
  applyExternalIds,
  buildDescriptionAndMetadata,
  buildOptionalSections,
  buildPricing,
} from './transform-request-sections';
import { applyAttributes, type AttributeWriteOptions } from './transform-request-attributes';

type Translate = (key: string) => string;

/**
 * Formats a positive count with singular/plural i18n labels, or empty when absent.
 */
function countFragment(count: number | undefined, singular: string, plural: string): string {
  if (!count || count <= 0) return '';
  return `${count} ${count > 1 ? plural : singular}`;
}

/**
 * Builds the ordered title fragments derived from a property's attributes.
 */
function collectTitleParts(values: PropertyFormValues, t: Translate): string[] {
  const bedrooms = values.bedrooms;
  const candidates = [
    values.category ?? '',
    bedrooms && bedrooms > 0 ? `${bedrooms} ${t('formDualPane.rightPanel.dorm')}` : '',
    countFragment(values.suites, t('formDualPane.rightPanel.suite'), t('formDualPane.rightPanel.suites')),
    countFragment(values.parking_spots, t('formDualPane.rightPanel.spot'), t('formDualPane.rightPanel.spots')),
    values.constructed_area ? `${values.constructed_area}m²` : '',
    values.neighborhood ?? '',
  ];
  return candidates.filter(Boolean);
}

/**
 * Generates a human-readable property title from its salient attributes.
 */
export function generateTitle(values: PropertyFormValues, t: Translate): string {
  return collectTitleParts(values, t).join(' · ');
}

/**
 * Formats a numeric value as Brazilian Real currency, or a dash when absent.
 */
export function formatCurrency(value: number | undefined): string {
  if (!value) return 'R$ —';
  return value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });
}

/**
 * Computes the rounded price per square meter, returning 0 when inputs are missing.
 */
export function computePricePerM2(
  salePrice: number | undefined,
  constructedArea: number | undefined,
): number {
  if (!salePrice || !constructedArea || constructedArea === 0) return 0;
  return Math.round(salePrice / constructedArea);
}

/**
 * Coerces a falsy value to undefined, preserving the original when truthy.
 */
function orUndef<T>(value: T): T | undefined {
  return value || undefined;
}

/**
 * Builds the location block from form values.
 */
function buildLocation(values: PropertyFormValues): PropertyNestedRequest['location'] {
  return {
    postal_code: orUndef(values.postal_code),
    street: orUndef(values.street),
    number: orUndef(values.number),
    complement: orUndef(values.complement),
    neighborhood: orUndef(values.neighborhood),
    city: orUndef(values.city),
    state: orUndef(values.state),
  };
}

/**
 * Builds the nested property request body from form values.
 *
 * @param values - Form values.
 * @param options - Pass `initial` (the values the form opened with) when
 *   updating, so an emptied Univen attribute is sent as null and cleared;
 *   without it (create, suggestions) empty attributes are left out.
 * @returns The nested request body.
 */
export function transformToRequest(
  values: PropertyFormValues,
  options: AttributeWriteOptions = {},
): PropertyNestedRequest {
  const request: PropertyNestedRequest = {};
  request.location = buildLocation(values);
  request.classification = {
    category: values.category || undefined,
    transaction_types: values.transaction_type?.length ? values.transaction_type : undefined,
  };
  request.characteristics = mapFormCharacteristicsToNested(
    values,
  ) as PropertyNestedRequest['characteristics'];
  request.amenities = mapFormAmenitiesToNested(values);
  const condo = buildCondominium(values);
  if (condo) request.condominium = condo;
  const pricing = buildPricing(values);
  if (pricing) request.pricing = pricing;
  buildDescriptionAndMetadata(values, request);
  buildOptionalSections(values, request);
  applyExternalIds(values, request);
  applyAttributes(values, request, options);
  return request;
}
